import React, { useEffect, useState } from 'react';
import fr from 'facet-rest';
import { addQuery, QueryVal, removeQuery } from 'Helpers/Query';
import useUserContext from 'Context/UserContext';
import Moment from 'moment';
import relationships from 'Consts/Relationships';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { useLocation } from 'react-router-dom';
import Schedule from './index';

function Container(props) {
  const { agentResource } = useUserContext();
  const location = useLocation();
  const { householdResource } = useSimpleHousehold();
  const [meetingResource, setMeeting] = useState(null);
  const [meetingCollection, setMeetingCollection] = useState(null);
  const [weeklyMeetingCollection, setWeeklyMeetingCollection] = useState(null);
  const [meetingCollectionLoading, setMeetingCollectionLoading] = useState(false);
  const [weeklyMeetingCollectionLoading, setWeeklyMeetingCollectionLoading] = useState(null);
  const [actionItemLinkCount, setActionItemLinkCount] = useState({});

  const [loadingPlannerActionItems, setLoadingPlannerActionItems] = useState(
    false,
  );

  const [loadingClientActionItems, setLoadingClientActionItems] = useState(
    false,
  );

  const [plannerActionItems, setPlannerActionItems] = useState([]);
  const [clientActionItems, setClientActionItems] = useState([]);
  const [meetingActionItems, setMeetingActionItems] = useState([]);
  const queryValDate = QueryVal('date');
  const queryValDailyView = QueryVal('dailyView');
  const [date, setDate] = useState(queryValDate ? new Moment(queryValDate) : new Moment());
  const [displayDailyView, setDisplayDailyView] = useState(queryValDailyView === 'true');

  function updateDate(updatedDate) {
    setDate(updatedDate);
    addQuery({ date: updatedDate.startOf('day').toISOString() });
    removeQuery('meetingId');
    setMeeting(null);
  }

  function handleIncrementDate() {
    updateDate(date.clone().add(1, 'd'));
  }

  function handleDecrementDate() {
    updateDate(date.clone().subtract(1, 'd'));
  }

  function handleResetDate() {
    updateDate(new Moment());
  }

  function handleIncrementWeek() {
    updateDate(date.clone().add(1, 'w'));
  }

  function handleDecrementWeek() {
    updateDate(date.clone().subtract(1, 'w'));
  }


  const toggleDailyView = (newVal) => {
    const dailyView = newVal === 'day';
    addQuery({ dailyView });
    setDisplayDailyView(dailyView);
    handleResetDate();
  };

  async function fetchMeeting() {
    setMeetingCollectionLoading(true);
    try {
      const params = {
        start: `gt|${date.startOf('day').toISOString()}`,
        end: `lt|${date.endOf('day').toISOString()}`,
        sort: 'start',
        limit: '15',
      };

      const collection = await agentResource.relationship(relationships.meetings)?.link('related')?.Get({
        params,
      }).catch((netErr) => {
        displayErrorMessage(netErr, netErr.errors);
      });
      setMeetingCollection(collection.resources().list());
      setMeetingCollectionLoading(false);
    } catch (err) {
      setMeetingCollectionLoading(true);
      throw err;
    }
  }

  async function fetchWeeklyMeetings() {
    setWeeklyMeetingCollectionLoading(true);
    try {
      const params = {
        start: `gt|${date.startOf('week').toISOString()}`,
        end: `lt|${date.endOf('week').toISOString()}`,
        limit: '75',
        sort: 'start',
      };

      const collection = await agentResource.relationship(relationships.meetings)?.link('related')?.Get({
        params,
      }).catch((netErr) => {
        displayErrorMessage(netErr, netErr.errors);
      });
      setWeeklyMeetingCollection(collection?.resources().list() ?? null);
      setWeeklyMeetingCollectionLoading(false);
    } catch (err) {
      setWeeklyMeetingCollectionLoading(false);
      throw err;
    }
  }

  const refreshActionItems = () => {
    setLoadingPlannerActionItems(true);
    setLoadingClientActionItems(true);
    if (!householdResource?.relationship('simpleActionItemsAssignedToHouseholdLinks')) {
      return;
    }
    householdResource
      .relationship('simpleActionItemsAssignedToHouseholdLinks')
      .link('related')
      .Get({ raw: true })
      .then((res) => {
        const allActionItems = res.data;
        const actionItemsAssignedToClient = allActionItems.map(ai => fr.resource(ai));
        setClientActionItems(actionItemsAssignedToClient);
        setLoadingClientActionItems(false);
      });

    householdResource
      .relationship('simpleActionItemsAssignedToFacetLinks')
      .link('related')
      .Get({ raw: true })
      .then((res) => {
        const allActionItems = res.data;
        const actionItemsAssignedToFacet = allActionItems.map(ai => fr.resource(ai));
        setPlannerActionItems(actionItemsAssignedToFacet);
      });

    if (meetingResource) {
      meetingResource.relationship('simpleActionItemLinks')
        .link('related')
        .Get({ raw: true })
        .then((res) => {
          const allActionItems = res.data;
          const actionItemsForMeeting = allActionItems.map(ai => ai.id);
          setMeetingActionItems(actionItemsForMeeting);
        });
    }
  };

  const onActionItemToggle = (meetingId, on) => {
    setActionItemLinkCount((current) => {
      const temp = Object.assign(current);
      temp[meetingId] = on ? temp[meetingId] + 1 : temp[meetingId] - 1;
      return temp;
    });
  };

  const getActionItemLinksFromScheduleEvents = async () => {
    if (meetingCollection) {
      const meetingIdToActionItemCount = await Promise.all(meetingCollection.map(async (res) => {
        const fetchedLinks = await res.relationship('simpleActionItemLinks').link('related').Get();
        return [res.id(), fetchedLinks.resources().list().length];
      }));
      setActionItemLinkCount(meetingIdToActionItemCount.reduce((acc, [meetingId, count]) => {
        acc[meetingId] = count;
        return acc;
      }, {}));
    }
  };

  useEffect(() => {
    getActionItemLinksFromScheduleEvents();
  }, [meetingCollection]);


  useEffect(() => {
    const queryId = QueryVal('meetingId');
    const modal = QueryVal('modal');
    if (modal) return;

    if (QueryVal('refresh') === 'action-item') {
      removeQuery('refresh');
      refreshActionItems();
    }
    if (meetingCollection && queryId) {
      const m = [...meetingCollection].find(x => x.id() === queryId);
      if (m) {
        setMeeting(m);
      }
    }
  }, [agentResource?.id(), location.search]);

  useEffect(() => {
    if (agentResource) {
      if (displayDailyView) {
        fetchMeeting();
      } else {
        fetchWeeklyMeetings();
      }
    }
  }, [agentResource?.id(), date]);

  useEffect(() => {
    if (meetingResource) {
      refreshActionItems();
    }
  }, [householdResource, meetingResource, displayDailyView]);

  return (
    <Schedule
      actionItemLinkCount={actionItemLinkCount}
      meetingResource={meetingResource}
      meetingActionItems={meetingActionItems}
      setMeetingActionItems={setMeetingActionItems}
      meetingCollection={meetingCollection || []}
      householdResource={householdResource}
      loadingPlannerActionItems={loadingPlannerActionItems}
      setLoadingPlannerActionItems={setLoadingPlannerActionItems}
      setPlannerActionItems={setPlannerActionItems}
      loadingClientActionItems={loadingClientActionItems}
      setLoadingClientActionItems={setLoadingClientActionItems}
      plannerActionItems={plannerActionItems}
      refreshActionItems={refreshActionItems}
      clientActionItems={clientActionItems}
      setClientActionItems={setClientActionItems}
      handleResetDate={handleResetDate}
      handleDecrementDate={handleDecrementDate}
      handleIncrementDate={handleIncrementDate}
      date={date}
      onActionItemToggle={onActionItemToggle}
      handleDecrementWeek={handleDecrementWeek}
      handleIncrementWeek={handleIncrementWeek}
      displayDailyView={displayDailyView}
      toggleDailyView={toggleDailyView}
      weeklyMeetingCollection={weeklyMeetingCollection}
      weeklyMeetingCollectionLoading={weeklyMeetingCollectionLoading}
      meetingCollectionLoading={meetingCollectionLoading}
      {...props}
    />
  );
}

export default Container;
